/* ══════════════════════════════════════════════════════════════
   API Client — HTTP client with in-memory caching
   Provides stale-while-revalidate for all frontend API calls
   ══════════════════════════════════════════════════════════════ */

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

/** Base URL for API calls — uses Vite proxy in dev, direct in prod */
function getBaseUrl(): string {
    return import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL || '');
}

/**
 * Fetch JSON from API endpoint with in-memory caching
 */
export async function apiFetch<T>(
    path: string,
    options: {
        cacheTTL?: number;          // seconds to cache (default: 30)
        staleWhileRevalidate?: boolean;  // serve stale data while fetching
        params?: Record<string, string>;
    } = {}
): Promise<{ data: T; cached: boolean; error?: string }> {
    const { cacheTTL = 30, staleWhileRevalidate = true, params } = options;

    // Build URL
    const url = new URL(`${getBaseUrl()}/api${path}`, window.location.origin);
    if (params) {
        Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }

    const cacheKey = url.toString();

    // Check cache
    const cached = cache.get(cacheKey) as CacheEntry<T> | undefined;
    const now = Date.now();

    if (cached) {
        const age = (now - cached.timestamp) / 1000;

        // Still fresh
        if (age < cached.ttl) {
            return { data: cached.data, cached: true };
        }

        // Stale but usable — return stale + revalidate
        if (staleWhileRevalidate && age < cached.ttl * 3) {
            // Fire background revalidation
            fetchAndCache<T>(cacheKey, url.toString(), cacheTTL).catch(() => { });
            return { data: cached.data, cached: true };
        }
    }

    // Fresh fetch
    try {
        const data = await fetchAndCache<T>(cacheKey, url.toString(), cacheTTL);
        return { data, cached: false };
    } catch (err) {
        // Return stale data on error if available
        if (cached) {
            return {
                data: cached.data,
                cached: true,
                error: `Using stale data: ${(err as Error).message}`,
            };
        }
        throw err;
    }
}

async function fetchAndCache<T>(cacheKey: string, url: string, ttl: number): Promise<T> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    cache.set(cacheKey, { data, timestamp: Date.now(), ttl });
    return data;
}

/**
 * Clear all cached data
 */
export function clearCache(): void {
    cache.clear();
}

/**
 * Clear specific cache entry
 */
export function invalidateCache(path: string): void {
    for (const key of cache.keys()) {
        if (key.includes(path)) {
            cache.delete(key);
        }
    }
}

/**
 * Get cache stats for debugging
 */
export function getCacheStats(): { entries: number; keys: string[] } {
    return {
        entries: cache.size,
        keys: Array.from(cache.keys()),
    };
}
